import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../contexts/AnalyticsContext';
import supabase from '../supabase/supabase';

const { FiFilter, FiCheck, FiChevronDown, FiXCircle } = FiIcons;

const ExclusionFilter = () => {
  const { excludedGroups, setExcludedGroups } = useAnalytics();
  const [availableGroups, setAvailableGroups] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      const { data } = await supabase.from('user_groups_config_1740000000004').select('name, color_code');
      if (data) setAvailableGroups(data);
    };
    fetchGroups();
  }, []);

  const toggleGroup = (name) => {
    if (excludedGroups.includes(name)) {
      setExcludedGroups(excludedGroups.filter(g => g !== name));
    } else {
      setExcludedGroups([...excludedGroups, name]);
    }
  };

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-3 px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all border-2 ${excludedGroups.length > 0 ? 'bg-red-50 border-red-200 text-red-600 shadow-lg shadow-red-100' : 'bg-white border-slate-100 text-slate-500 hover:border-indigo-200'}`}
      >
        <SafeIcon icon={FiFilter} />
        {excludedGroups.length > 0 ? `Excluding ${excludedGroups.length} Groups` : 'User Exclusion Filter'}
        <SafeIcon icon={FiChevronDown} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
            <motion.div 
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-4 w-72 bg-white rounded-[2rem] shadow-2xl border border-slate-100 z-50 overflow-hidden"
            >
              <div className="p-6 bg-slate-50 border-b border-slate-100">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select groups to exclude</h4>
              </div>
              <div className="p-4 space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                {availableGroups.map((group) => (
                  <button
                    key={group.name}
                    onClick={() => toggleGroup(group.name)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${excludedGroups.includes(group.name) ? 'bg-red-50 text-red-600' : 'hover:bg-slate-50 text-slate-700'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color_code }}></div>
                      <span className="text-xs font-black capitalize">{group.name.replace('_', ' ')}</span>
                    </div>
                    {excludedGroups.includes(group.name) && <SafeIcon icon={FiCheck} />}
                  </button>
                ))}
              </div>
              {excludedGroups.length > 0 && (
                <button 
                  onClick={() => setExcludedGroups([])}
                  className="w-full p-4 bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiXCircle} /> Reset Filter
                </button>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExclusionFilter;